import { FeatureTypeEnum, MenuTypeEnum } from '@/api/hanly/hanlyAPI.schemas';
import { useFeatureFlag } from './use-feature-flag';
import { useMenuVisibility } from './use-menu-visibility';

// 受注が非表示で、かつ他に表示メニューが一つも無い（ほぼ異常系）場合のフォールバック遷移先
const FALLBACK_PATH = '/orders';

/**
 * ログイン後・`/` アクセス時・ロゴクリック時の遷移先を返すフック。
 *
 * 通常は受注(/orders)を初期遷移先にする。受注を非表示にした組織のみ、ヘッダーの
 * メニュー（src/components/header.tsx）と同じ並び順で左から最初に表示されるメニュー
 * へフォールバックする。Demo機能（会話・insights）は候補から除外する。
 * それでも一つも表示されない場合は `/orders` 固定（ほぼ異常系）。
 *
 * NOTE: 候補の並び順・表示条件はヘッダーのメニュー定義と一致させること。
 */
export const useDefaultMenuPath = (): string => {
  const { isEnabled } = useFeatureFlag();
  const { isVisible } = useMenuVisibility();

  // 通常は受注を初期遷移先にする
  if (isVisible(MenuTypeEnum.ORDERS)) {
    return '/orders';
  }

  // 受注が非表示の組織は、ヘッダーの並び順（左→右）で最初に表示されるメニューへ。
  // 会話・insights(探索) はDemo機能のため候補から除外する。
  const candidates: { path: string; visible: boolean }[] = [
    { path: '/leads', visible: isEnabled(FeatureTypeEnum.CRM) }, // 見込客
    { path: '/projects', visible: isVisible(MenuTypeEnum.PROJECTS) }, // 案件
    { path: '/purchase-orders', visible: isVisible(MenuTypeEnum.PURCHASE_ORDERS) }, // 発注
    { path: '/inventory', visible: isVisible(MenuTypeEnum.INVENTORY) }, // 在庫
    { path: '/manufacturing-orders', visible: isVisible(MenuTypeEnum.MANUFACTURING) }, // 生産
    { path: '/products', visible: isVisible(MenuTypeEnum.PRODUCTS) }, // 商品
    { path: '/customers', visible: isVisible(MenuTypeEnum.CUSTOMERS) }, // 取引先
    { path: '/explore', visible: isVisible(MenuTypeEnum.EXPLORE) }, // 探索
  ];

  return candidates.find((candidate) => candidate.visible)?.path ?? FALLBACK_PATH;
};
